import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "../api/axiosInstance";
import {
  GoogleMap,
  Marker,
  LoadScript,
  Polyline,
} from "@react-google-maps/api";
import polyline from "@mapbox/polyline";

const containerStyle = {
  width: "100%",
  height: "500px",
};

const FALLBACK_CENTER = {
  lat: 28.367,
  lng: 79.4304,
};

export default function AdminLiveMap() {
  const { orderId } = useParams();

  const [order, setOrder] = useState(null);
  const [animatedPos, setAnimatedPos] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  const [eta, setEta] = useState(null);
  const [distance, setDistance] = useState(null);

  const animationRef = useRef(null);

  /* ---------------- FETCH ORDER ---------------- */
  const fetchOrder = async () => {
    try {
      const res = await axios.get(`admin/live-map/${orderId}/`);
      const data = res.data;
      setOrder(data);

      const boyLat =
        data.delivery_boy_location?.lat ??
        data.delivery_boy_location?.latitude;

      const boyLng =
        data.delivery_boy_location?.lng ??
        data.delivery_boy_location?.longitude;

      if (boyLat && boyLng) {
        const newPos = {
          lat: Number(boyLat),
          lng: Number(boyLng),
        };

        if (animatedPos) {
          animateMarker(animatedPos, newPos);
        } else {
          setAnimatedPos(newPos);
        }
      }
    } catch (err) {
      console.error("Live map fetch failed", err);
    }
  };

  /* ---------------- FETCH REAL ROAD ROUTE ---------------- */
  const fetchRoute = async () => {
    try {
      const res = await axios.get("delivery/route/", {
        params: { order_id: orderId },
      });

      if (!res.data?.polyline) return;

      const decodedPath = polyline
        .decode(res.data.polyline)
        .map(([lat, lng]) => ({ lat, lng }));

      setRoutePath(decodedPath);
      setEta(res.data.eta_minutes);
      setDistance(res.data.distance_km);
    } catch (err) {
      console.error("Route fetch failed", err);
    }
  };

  /* ---------------- SMOOTH MARKER ---------------- */
  const animateMarker = (from, to) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const steps = 60;
    let stepCount = 0;

    const deltaLat = (to.lat - from.lat) / steps;
    const deltaLng = (to.lng - from.lng) / steps;

    const step = () => {
      stepCount++;

      setAnimatedPos({
        lat: from.lat + deltaLat * stepCount,
        lng: from.lng + deltaLng * stepCount,
      });

      if (stepCount < steps) {
        animationRef.current = requestAnimationFrame(step);
      }
    };

    step();
  };

  /* ---------------- AUTO REFRESH ---------------- */
  useEffect(() => {
    fetchOrder();
    fetchRoute();

    const interval = setInterval(() => {
      fetchOrder();
      fetchRoute();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  if (!order) return <p style={{ padding: 20 }}>Loading map...</p>;

  const center = animatedPos || FALLBACK_CENTER;

  /* -------- SAFE COORD EXTRACTION -------- */
  const pickupLat = order.pickup?.lat ?? order.pickup_lat;
  const pickupLng = order.pickup?.lng ?? order.pickup_lng;

  const customerLat = order.customer?.lat ?? order.customer_lat;
  const customerLng = order.customer?.lng ?? order.customer_lng;

  return (
    <div style={{ padding: 20 }}>
      <h2>Live Tracking – Order #{orderId}</h2>

      {/* ETA + DISTANCE */}
      {eta && (
        <div
          style={{
            background: "#f1f5f9",
            padding: "10px 15px",
            borderRadius: 8,
            marginBottom: 10,
            display: "inline-block",
            fontWeight: "bold",
          }}
        >
          🚚 ETA: {eta} min | Distance: {distance} km
        </div>
      )}

      <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={14}
        >
          {/* PICKUP POINT */}
          {pickupLat && pickupLng && (
            <Marker
              position={{
                lat: Number(pickupLat),
                lng: Number(pickupLng),
              }}
              label="P"
            />
          )}

          {/* CUSTOMER POINT (FIXED) */}
          {customerLat && customerLng && (
            <Marker
              position={{
                lat: Number(customerLat),
                lng: Number(customerLng),
              }}
              label="C"
            />
          )}

          {/* DELIVERY BOY */}
          {animatedPos && <Marker position={animatedPos} label="D" />}

          {/* REAL ROAD ROUTE */}
          {routePath.length > 0 && (
            <Polyline
              path={routePath}
              options={{
                strokeColor: "#2563eb",
                strokeOpacity: 0.9,
                strokeWeight: 6,
              }}
            />
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
}
